import React, { useEffect, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import cn from 'classnames'
import type { SuggestionType } from 'src/types/search'
import { SearchMode } from 'src/types/search'
import { SuggesterReadOnly } from './modes/ReadOnly'
import { SuggesterBorderLess } from './modes/BorderLess'
import { SuggesterFull } from './modes/Full'
import Suggestion from './Suggestion'
import { useClickAway } from 'react-use'
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock'
import { useKafkaEvent } from 'src/hooks/useKafkaEvent'
import { SECTIONS } from 'src/hooks/useKafkaEvent/constants'
import { usePage } from 'src/hooks/usePage'
import { LoadingSpinner } from './LoadingSpinner'
import { NoResults } from './NoResults'
import { useTopDestinations } from 'src/hooks/useTopDestinations'
import { TopDestination } from './TopDestination'
import type { TopDestination as TopDestinationModel } from '@s4l/stayforlong/marketing/TopDestination'
import { UserSearchItem } from './UserSearch'
import { getSearchModeClass } from './utils'
import type { UserSearch } from 'src/context/search/utils'
import { useSuggesterSearch } from 'src/hooks/useSuggesterSearch'

import styles from './styles.module.css'

interface ISuggesterProps {
  readonly mode: SearchMode
  readonly className?: string
  readonly suggestion?: SuggestionType | null
  readonly suggestions: SuggestionType[]
  readonly onSelect: (value: SuggestionType | null) => void
  readonly onChangeInput: (search: string) => void
  readonly hasError?: boolean
  readonly isFetchingSuggestions?: boolean
  readonly suggesterSearch?: string
  readonly isFirstSearch?: boolean
}

const suggesterConfig = {
  [SearchMode.Readonly]: SuggesterReadOnly,
  [SearchMode.Borderless]: SuggesterBorderLess,
  [SearchMode.Full]: SuggesterFull,
}

export const TOP_DESTINATIONS_LIMIT = 5

function Suggester({
  mode,
  suggestions,
  onSelect,
  className = '',
  hasError,
  isFetchingSuggestions = false,
  suggesterSearch = '',
  isFirstSearch = false,
  ...props
}: ISuggesterProps) {
  const suggesterRef = useRef<HTMLDivElement | null>(null)
  const [hasFocus, setHasFocus] = useState(false)
  const selectedTopDestinationRef = useRef<number | undefined>(undefined)

  const { pageId } = usePage()
  const { topDestinationsJSON } = useTopDestinations()
  const topDestinations = topDestinationsJSON?.list

  const { userLastSearches, removeUserSearch } = useSuggesterSearch()

  const { sendEvent } = useKafkaEvent({ page: pageId })

  const isReadOnly = mode === SearchMode.Readonly
  const hasSuggesterText = suggesterSearch !== ''

  const showSuggestions = !isFirstSearch && !isReadOnly && hasFocus && hasSuggesterText
  const showTopDestinations = hasFocus && suggestions.length === 0 && !!topDestinations?.length
  const showNoResults = suggestions.length === 0 && hasSuggesterText
  const showLastSearches = !isFetchingSuggestions && suggestions.length === 0 && !hasSuggesterText

  useEffect(() => {
    if (!hasFocus && suggesterRef.current) {
      enableBodyScroll(suggesterRef.current)
    }
  }, [hasFocus])

  useClickAway(suggesterRef, () => {
    const hasSuggestions = suggestions.length > 0
    const hasSelected = props.suggestion

    if (hasFocus) {
      if (hasSuggestions && !hasSelected?.ID) {
        const [firstSuggestion] = suggestions

        onSelect(firstSuggestion)
      } else {
        onSelect(null)
        onSelect(hasSelected ?? null)
      }

      setHasFocus(false)
      if (suggesterRef.current) {
        enableBodyScroll(suggesterRef.current)
      }
    }
  })

  const SuggesterVariant = suggesterConfig[mode]

  function focusInput(input?: HTMLInputElement) {
    if (!input) return

    // this prevent IOS dynamic heights to recalculate and handle properly
    input.blur()

    requestAnimationFrame(() => {
      suggesterRef?.current?.getElementsByTagName('input')?.[0]?.focus?.()
    })
  }

  const handleFocus = () => {
    if (!hasFocus) {
      sendEvent({ section: SECTIONS.SUGGESTER })
      setHasFocus(true)

      if (suggesterRef.current) {
        if (mode !== SearchMode.Borderless) {
          disableBodyScroll(suggesterRef.current)
        }
        const input = suggesterRef?.current?.getElementsByTagName('input')?.[0]
        focusInput(input)
      }
    }
  }

  const onClear = () => {
    onSelect(null)
    sendEvent(
      { section: SECTIONS.SUGGESTER_RESET },
      { text: suggesterRef?.current?.getElementsByTagName('input')?.[0]?.value ?? '' }
    )
    requestAnimationFrame(() => {
      suggesterRef?.current?.getElementsByTagName('input')?.[0]?.focus?.()
    })
  }

  const createSelectHandler = (suggestion: SuggestionType, index: number) => () => {
    onSelect(suggestion)
    props.onChangeInput(suggestion.Text)
    const { Text, Description, ID, Type } = suggestion
    sendEvent(
      { section: SECTIONS.SUGGESTION },
      { suggestion: { Text, Description, ID, Type }, search: suggesterSearch, index }
    )
    setHasFocus(false)
    if (suggesterRef.current) {
      enableBodyScroll(suggesterRef.current)
    }
  }

  const handleSelectTopDestination = (
    destination: ReturnType<TopDestinationModel['toJSON']>,
    index: number
  ) => {
    props.onChangeInput(destination.name)
    selectedTopDestinationRef.current = index
  }

  useEffect(() => {
    if (selectedTopDestinationRef.current !== undefined && suggestions.length > 0) {
      const [suggestion] = suggestions
      selectedTopDestinationRef.current = undefined
      props.onChangeInput(suggestion.Text)
      onSelect(suggestion)
      sendEvent(
        { section: SECTIONS.SUGGESTION_POPULAR_DES },
        { suggestion, search: suggesterSearch, index: 0 }
      )

      setHasFocus(false)
      if (suggesterRef.current) {
        enableBodyScroll(suggesterRef.current)
      }
    }
  }, [suggestions]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectSearch = (search: UserSearch, index: number) => {
    const {
      id: ID,
      text: Text,
      description: Description,
      slug: SlugCitySlug,
      country: Country,
      type: Type,
      citySlug,
      coordinates,
      boxShape,
    } = search.location

    const [Slug, CitySlug] = SlugCitySlug.split('_')

    const suggestion: SuggestionType = {
      ID,
      Text,
      Type,
      Slug,
      Country,
      Coordinate: { lat: coordinates.lat, lon: coordinates.lon },
      Description,
      Stars: 0,
      CitySlug: CitySlug ?? citySlug ?? '',
      ...(boxShape && {
        BoxShape: {
          bottomRight: {
            lat: boxShape.bottomRight.lat,
            lon: boxShape.bottomRight.lon,
          },
          topLeft: {
            lat: boxShape.topLeft.lat,
            lon: boxShape.topLeft.lon,
          },
        },
      }),
    }

    props.onChangeInput(Text)
    onSelect(suggestion)
    sendEvent(
      { section: SECTIONS.SUGGESTION_SEARCH_DES },
      { suggestion, search: suggesterSearch, index }
    )
    setHasFocus(false)
    if (suggesterRef.current) {
      enableBodyScroll(suggesterRef.current)
    }
  }

  const searchModeClass = getSearchModeClass(mode)

  const showSuggestionsPopup = showSuggestions || showLastSearches || showTopDestinations

  return (
    <div
      ref={suggesterRef}
      role="button"
      tabIndex={0}
      data-testid="suggester-wrapper"
      className={cn(className, styles.suggestionWrapper, searchModeClass, {
        'has-focus': hasFocus,
        [styles.hasFocus]: hasFocus,
        'has-error': hasError && !hasFocus,
        [styles.hasError]: hasError && !hasFocus,
      })}
    >
      <SuggesterVariant
        {...props}
        suggesterSearch={suggesterSearch}
        hasFocus={hasFocus}
        onClose={() => setHasFocus(false)}
        onClear={onClear}
        hasError={hasError && !hasFocus}
        onFocus={handleFocus}
      />

      {isFetchingSuggestions && hasFocus && <LoadingSpinner />}
      {showSuggestionsPopup && (
        <ul className={cn(styles.suggestions, searchModeClass, { [styles.hasFocus]: hasFocus })}>
          {showSuggestions && (
            <>
              {showNoResults && <NoResults />}
              {suggestions.map((suggestion, index) => (
                <Suggestion
                  key={suggestion.ID}
                  item={suggestion}
                  onClick={createSelectHandler(suggestion, index)}
                />
              ))}
            </>
          )}
          {showLastSearches &&
            userLastSearches.map((search, index) => (
              <UserSearchItem
                key={search.location.id}
                search={search}
                onClick={() => handleSelectSearch(search, index)}
                onRemove={() => removeUserSearch(search.location.id)}
              />
            ))}
          {showTopDestinations && (
            <>
              <li className={styles.topDestinationsTitleContainer} tabIndex={undefined}>
                <h4 className={styles.topDestinationsTitle}>
                  <FormattedMessage id="global.popular-destinations" />
                </h4>
              </li>
              {topDestinations?.slice(0, TOP_DESTINATIONS_LIMIT).map((destination, index) => (
                <TopDestination
                  key={destination.slug}
                  item={destination}
                  onClick={() => handleSelectTopDestination(destination, index)}
                />
              ))}
            </>
          )}
        </ul>
      )}
    </div>
  )
}

export default Suggester
